import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, QuoteIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const testimonialAuthors = [{
  author: 'Анна Коваленко',
  company: 'TechCorp',
  companyInitial: 'T',
  companyColor: 'bg-blue-500'
}, {
  author: 'Дмитро Петров',
  company: 'FinGroup',
  companyInitial: 'F',
  companyColor: 'bg-emerald-500'
}, {
  author: 'Олена Сидоренко',
  company: 'DataFlow',
  companyInitial: 'D',
  companyColor: 'bg-violet-500'
}, {
  author: 'Максим Іванов',
  company: 'ScaleUp',
  companyInitial: 'S',
  companyColor: 'bg-amber-500'
}];
const clientLogos = [{
  name: 'TechCorp',
  initial: 'TC',
  color: 'bg-blue-50 text-blue-600 border-blue-100'
}, {
  name: 'FinGroup',
  initial: 'FG',
  color: 'bg-emerald-50 text-emerald-600 border-emerald-100'
}, {
  name: 'DataFlow',
  initial: 'DF',
  color: 'bg-violet-50 text-violet-600 border-violet-100'
}, {
  name: 'ScaleUp',
  initial: 'SU',
  color: 'bg-amber-50 text-amber-600 border-amber-100'
}, {
  name: 'MedTech',
  initial: 'MT',
  color: 'bg-rose-50 text-rose-600 border-rose-100'
}, {
  name: 'LogiPro',
  initial: 'LP',
  color: 'bg-cyan-50 text-cyan-600 border-cyan-100'
}, {
  name: 'RetailX',
  initial: 'RX',
  color: 'bg-orange-50 text-orange-600 border-orange-100'
}, {
  name: 'BuildCo',
  initial: 'BC',
  color: 'bg-teal-50 text-teal-600 border-teal-100'
}];
export function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const next = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % t.testimonials.items.length);
  };
  const prev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + t.testimonials.items.length) % t.testimonials.items.length);
  };
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, []);
  const testimonial = t.testimonials.items[current];
  const author = testimonialAuthors[current];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0
    })
  };
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-507">
      <div className="absolute inset-0 pointer-events-none" data-id="element-508">
        <div className="absolute top-0 left-1/3 w-[400px] h-[400px] rounded-full bg-primary-50/25 blur-3xl" data-id="element-509" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-cyan-50/20 blur-3xl" data-id="element-510" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-511">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-512">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-513">
            {t.testimonials.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-514">
            {t.testimonials.title}
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-515">
          {clientLogos.map(logo => <div key={logo.name} className={`w-16 h-16 rounded-xl ${logo.color} border flex items-center justify-center text-sm font-bold shadow-sm`} title={logo.name} data-id="element-516">
              {logo.initial}
            </div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="max-w-3xl mx-auto" data-id="element-517">
          <div className="relative bg-gradient-to-br from-navy-50/80 via-white to-primary-50/30 rounded-3xl p-8 sm:p-12 border border-navy-100 shadow-lg shadow-navy-100/30 overflow-hidden" data-id="element-518">
            <QuoteIcon className="absolute top-6 right-8 w-32 h-32 text-primary-100/40" data-id="element-519" />
            <QuoteIcon className="relative z-10 w-10 h-10 text-primary-400 mb-6" data-id="element-520" />

            <div className="min-h-[160px] flex items-center relative z-10" data-id="element-521">
              <AnimatePresence mode="wait" custom={direction} data-id="element-522">
                <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35
              }} data-id="element-523">
                  <blockquote className="text-lg sm:text-xl text-navy-700 leading-relaxed font-medium" data-id="element-524">
                    «{testimonial.quote}»
                  </blockquote>
                  <div className="mt-8 flex items-center gap-4" data-id="element-525">
                    <div className={`w-12 h-12 rounded-full ${author.companyColor} flex items-center justify-center text-white font-bold text-sm shadow-lg`} data-id="element-526">
                      {author.companyInitial}
                    </div>
                    <div data-id="element-527">
                      <div className="font-bold text-navy-900" data-id="element-528">
                        {author.author}
                      </div>
                      <div className="text-sm text-navy-500" data-id="element-529">
                        {testimonial.role}, {author.company}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-between mt-8 relative z-10" data-id="element-530">
              <div className="flex gap-2" data-id="element-531">
                {t.testimonials.items.map((_, i) => <button key={i} onClick={() => {
                setDirection(i > current ? 1 : -1);
                setCurrent(i);
              }} className={`h-2.5 rounded-full transition-all duration-300 ${i === current ? 'bg-primary-500 w-8 shadow-sm shadow-primary-500/30' : 'bg-navy-300/60 w-2.5 hover:bg-navy-400'}`} aria-label={`Testimonial ${i + 1}`} data-id="element-532" />)}
              </div>
              <div className="flex gap-2" data-id="element-533">
                <button onClick={prev} className="w-10 h-10 rounded-xl bg-white border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-50 hover:border-navy-300 transition-all shadow-sm" data-id="element-534">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-535" />
                </button>
                <button onClick={next} className="w-10 h-10 rounded-xl bg-white border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-50 hover:border-navy-300 transition-all shadow-sm" data-id="element-536">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-537" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}